/**
 * The mahalla list used by every filter dropdown and picker.
 *
 * Reads the PUBLIC `GET /mahallas` route, not `GET /admin/mahallas`. That is
 * deliberate: the admin route is capability `mahalla.write` (role `admin` only),
 * but a `handler` filtering their inbox by mahalla needs the names too. The
 * public route carries no PII (§2.2) and is the same data.
 */
import { useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import type { Locale } from '@navoiy/domain';
import { api, unwrap } from '../api/client.js';

export type MahallaSummary = components['schemas']['MahallaSummary'];

export function useMahallas(locale: Locale): {
  mahallas: MahallaSummary[];
  loading: boolean;
} {
  const [mahallas, setMahallas] = useState<MahallaSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const data = unwrap(await api().GET('/mahallas', { params: { query: { locale } } }));
        if (!cancelled) setMahallas(data.items);
      } catch {
        if (!cancelled) setMahallas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  return { mahallas, loading };
}
